
import store from "@/store";
import Vue from "vue";
import Component from 'vue-class-component';
import OrdersTab from './OrdersTab.vue';
import Summary from "./Summary.vue";
import InvoicesTab from "./InvoicesTab.vue";
import SessionSortBtn from "../SessionSortBtn.vue";
import UploadModule from '@/store/modules/Upload';
import { getModule } from 'vuex-module-decorators';
import { createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';

const uploadModule = getModule(UploadModule, store);

@Component({
    components: {
        OrdersTab,
        Summary,
        InvoicesTab,
        SessionSortBtn,
    }
})
export default class CommandCenter extends Vue {
    public currentTab = null;
    public show = false;
    public isLeavingCommandCenter = false;

    currentTime: Date = new Date();
    private currentClockTimer: number | null = null;
    private etaTimer: number | null = null;
    private lastProgressSample: { progress: number; ts: number } | null = null;
    private remainingSeconds: number | null = null;
    private speedEma: number | null = null;
    private leaveAnimationListener: EventListener | null = null;

    public mounted() {
        this.leaveAnimationListener = () => {
            this.isLeavingCommandCenter = true;
        };
        window.addEventListener('raws-command-center-leave', this.leaveAnimationListener);

        this.currentTime = new Date();
        const updateTime = () => {
            this.currentTime = new Date();
            const nextMinute = new Date();
            nextMinute.setSeconds(0, 0);
            nextMinute.setMinutes(nextMinute.getMinutes() + 1);
            this.currentClockTimer = window.setTimeout(updateTime, nextMinute.getTime() - this.currentTime.getTime());
        };
        updateTime();
        this.startEtaTracking();
    }

    public beforeDestroy() {
        if (this.leaveAnimationListener) {
            window.removeEventListener('raws-command-center-leave', this.leaveAnimationListener);
            this.leaveAnimationListener = null;
        }
        if (this.currentClockTimer) {
            clearTimeout(this.currentClockTimer);
        }
        if (this.etaTimer) {
            clearInterval(this.etaTimer);
        }
    }

    public playSlideSound() {
        const audio = createInterfaceSound('/sound/slide.wav');
        playInterfaceSound(audio).catch(() => undefined);
    }

    get hours() {
        if (this.isUploadProgressVisible) {
            return this.etaMinutes;
        }
        return this.currentTime.getHours();
    }

    get minutes() {
        if (this.isUploadProgressVisible) {
            return this.etaSeconds;
        }
        const minutes = this.currentTime.getMinutes();
        return String(minutes).padStart(2, '0');
    }

    get uploadInProgress() {
        return uploadModule.photoQueue.some(({ status }) => status !== 'finished');
    }

    get overallProgress() {
        return uploadModule.overallProgress;
    }

    get isUploadProgressVisible() {
        return this.overallProgress > 0 && this.overallProgress < 100;
    }

    get hasActiveConflicts() {
        return uploadModule.conflicts.length > 0;
    }

    get etaMinutes() {
        if (this.remainingSeconds === null) {
            return '--';
        }
        return String(Math.floor(this.remainingSeconds / 60)).padStart(2, '0');
    }

    get etaSeconds() {
        if (this.remainingSeconds === null) {
            return '--';
        }
        return String(this.remainingSeconds % 60).padStart(2, '0');
    }

    private startEtaTracking() {
        this.etaTimer = window.setInterval(() => {
            const now = Date.now();
            const progress = this.overallProgress;

            if (!this.isUploadProgressVisible) {
                this.lastProgressSample = null;
                this.remainingSeconds = null;
                this.speedEma = null;
                return;
            }

            // Freeze ETA while conflict dialog is active.
            if (this.hasActiveConflicts) {
                return;
            }

            if (!this.lastProgressSample) {
                this.lastProgressSample = { progress, ts: now };
                return;
            }

            const progressDelta = progress - this.lastProgressSample.progress;
            const timeDeltaSec = (now - this.lastProgressSample.ts) / 1000;

            if (progressDelta > 0.01 && timeDeltaSec > 0) {
                const instantSpeed = progressDelta / timeDeltaSec;
                const alpha = 0.2;
                this.speedEma = this.speedEma === null
                    ? instantSpeed
                    : (alpha * instantSpeed) + ((1 - alpha) * this.speedEma);

                const nextEstimate = Math.ceil(Math.max(0, 100 - progress) / this.speedEma);
                if (this.remainingSeconds === null) {
                    this.remainingSeconds = nextEstimate;
                } else {
                    const maxIncreasePerTick = 2;
                    const maxDecreasePerTick = 5;
                    const delta = nextEstimate - this.remainingSeconds;
                    if (delta > maxIncreasePerTick) {
                        this.remainingSeconds += maxIncreasePerTick;
                    } else if (delta < -maxDecreasePerTick) {
                        this.remainingSeconds -= maxDecreasePerTick;
                    } else {
                        this.remainingSeconds = nextEstimate;
                    }
                }
            } else if (this.remainingSeconds !== null && this.remainingSeconds > 0) {
                this.remainingSeconds = Math.max(0, this.remainingSeconds - 1);
            }

            this.lastProgressSample = { progress, ts: now };
        }, 1000);
    }
}
